import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Delete, LogIn, LogOut, Clock, CalendarDays, ArrowLeft, Coffee } from "lucide-react";
import { openWhatsApp } from "@/lib/whatsapp";
import Logo from "@/components/Logo";

const KIOSK_BG = "/kiosk-bg.webp";

function useNow() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return now;
}

const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "clear", "0", "del"];

export default function KioskPage() {
  const now = useNow();
  const [pin, setPin] = useState("");
  const [feedback, setFeedback] = useState(null); // {type, title, subtitle}
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("attendance"); // 'attendance' | 'break' | 'leave'
  const [ownerPhone, setOwnerPhone] = useState("");
  const [leaveForm, setLeaveForm] = useState({ start_date: "", end_date: "", reason: "" });
  const startRef = useRef(null);

  useEffect(() => {
    api.get("/settings/public")
      .then(({ data }) => setOwnerPhone(data.owner_phone || ""))
      .catch(() => {});
  }, []);

  const todayIso = new Date().toISOString().slice(0, 10);

  const timeStr = now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
  const dateStr = now.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  const handleKey = (k) => {
    if (busy) return;
    if (k === "clear") return setPin("");
    if (k === "del") return setPin((p) => p.slice(0, -1));
    setPin((p) => (p.length >= 6 ? p : p + k));
  };

  const submit = async () => {
    if (pin.length < 4) {
      setFeedback({ type: "error", title: "PIN too short", subtitle: "Enter your 4–6 digit PIN." });
      return;
    }
    if (mode === "leave") {
      if (!leaveForm.start_date || !leaveForm.end_date) {
        setFeedback({ type: "error", title: "Missing dates", subtitle: "Pick a start and end date." });
        return;
      }
      setBusy(true);
      try {
        const { data } = await api.post("/leave-requests", {
          pin,
          start_date: leaveForm.start_date,
          end_date: leaveForm.end_date,
          reason: leaveForm.reason,
        });
        setFeedback({
          type: "leave",
          title: `Leave request sent, ${data.employee_name}`,
          subtitle: `${data.start_date} to ${data.end_date} · awaiting approval`,
          notify: {
            phone: ownerPhone,
            message: `Royal Wow Meals: ${data.employee_name} (${data.employee_role}) has applied for leave from ${data.start_date} to ${data.end_date}. Reason: ${data.reason || "—"}. Please review.`,
          },
        });
        setPin("");
        setLeaveForm({ start_date: "", end_date: "", reason: "" });
        setMode("attendance");
      } catch (err) {
        setFeedback({
          type: "error",
          title: "Could not submit leave",
          subtitle: formatApiErrorDetail(err?.response?.data?.detail) || "Please try again.",
        });
      } finally {
        setBusy(false);
      }
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/attendance/punch", { pin });
      if (data.action === "check_in") {
        setFeedback({
          type: "in",
          title: `Welcome, ${data.employee.name}`,
          subtitle: `Checked in at ${new Date(data.record.check_in).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`,
        });
      } else if (data.action === "check_out") {
        setFeedback({
          type: "out",
          title: `Goodbye, ${data.employee.name}`,
          subtitle: `${data.record.hours} hours today. Well done.`,
        });
      } else {
        setFeedback({
          type: "info",
          title: `${data.employee.name}`,
          subtitle: `Already checked out today.`,
        });
      }
      setPin("");
    } catch (err) {
      setFeedback({
        type: "error",
        title: "Invalid PIN",
        subtitle: formatApiErrorDetail(err?.response?.data?.detail) || "Please try again.",
      });
      setPin("");
    } finally {
      setBusy(false);
      setTimeout(() => setFeedback(null), 3500);
    }
  };

  return (
    <div className="dark min-h-screen relative overflow-hidden bg-[#0A0A0A] text-white" data-testid="kiosk-page">
      <img
        src={KIOSK_BG}
        alt="Royal Wow Meals dining room"
        className="absolute inset-0 w-full h-full object-cover opacity-55"
        aria-hidden="true"
      />
      <div className="absolute inset-0 bg-black/70 kiosk-grain" aria-hidden="true" />

      <div className="relative z-10 min-h-screen grid lg:grid-cols-2">
        {/* Left: brand + clock */}
        <div className="flex flex-col justify-between p-10 lg:p-16">
          <div className="flex items-center gap-3 fade-up">
            <Logo variant="mark" className="w-10 h-10 ring-white/20" />
            <span className="tracking-[0.3em] text-xs text-white/70">ROYAL WOW MEALS · ATTENDANCE</span>
          </div>

          <div className="fade-up" style={{ animationDelay: "100ms" }}>
            <div className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-4 flex items-center gap-2">
              <Clock className="w-3.5 h-3.5" aria-hidden="true" /> Live
            </div>
            <div className="font-serif text-[8rem] leading-none tracking-tight" data-testid="kiosk-time">
              {timeStr}
            </div>
            <div className="mt-4 text-white/70 text-lg">{dateStr}</div>
          </div>

          <div className="fade-up flex items-center justify-between text-xs text-white/50">
            <span>Enter your 4–6 digit PIN to check in or out.</span>
            <Link to="/login" data-testid="manager-login-link" className="text-[#D4AF37] hover:underline underline-offset-4">
              Manager login →
            </Link>
          </div>
        </div>

        {/* Right: pin entry */}
        <div className="flex flex-col justify-center items-center p-10 lg:p-16">
          <div className="w-full max-w-sm fade-up" style={{ animationDelay: "200ms" }}>
            {mode === "attendance" ? (
              <>
                <p className="text-white/60 text-xs tracking-[0.3em] uppercase mb-3 text-center">Enter PIN</p>

                {/* PIN dots */}
                <div className="flex justify-center gap-4 mb-10" data-testid="pin-dots">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div
                      key={i}
                      className={`w-4 h-4 rounded-full border-2 transition-colors ${
                        i < pin.length ? "bg-[#D4AF37] border-[#D4AF37]" : "border-white/30"
                      }`}
                      aria-hidden="true"
                    />
                  ))}
                </div>

                {/* Numpad */}
                <div className="grid grid-cols-3 gap-4">
                  {KEYS.map((k) => (
                    <button
                      key={k}
                      type="button"
                      data-testid={`pin-key-${k}`}
                      onClick={() => handleKey(k)}
                      className="key-press h-20 rounded-2xl bg-white/5 hover:bg-white/10 backdrop-blur-md border border-white/10 text-3xl font-serif transition-colors flex items-center justify-center"
                    >
                      {k === "del" ? (
                        <Delete className="w-6 h-6" aria-hidden="true" />
                      ) : k === "clear" ? (
                        <span className="text-xs tracking-widest">CLR</span>
                      ) : (
                        k
                      )}
                    </button>
                  ))}
                </div>

                <button
                  type="button"
                  disabled={busy || pin.length < 4}
                  data-testid="pin-submit-btn"
                  onClick={submit}
                  className="mt-6 w-full h-16 rounded-full bg-[#D4AF37] text-black text-lg font-medium tracking-wide hover:bg-[#E5C349] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {busy ? "…" : "Punch in / out"}
                </button>

                <div className="mt-4 grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    disabled={busy || pin.length < 4}
                    onClick={async () => {
                      if (pin.length < 4) return;
                      setBusy(true);
                      try {
                        const { data } = await api.post("/attendance/break", { pin });
                        if (data.action === "break_start") {
                          const t = new Date(data.record.break_start).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
                          setFeedback({
                            type: "break_start",
                            title: `Break started at ${t}, ${data.employee.name}`,
                            subtitle: "Tap Punch Break again with your PIN to end the break.",
                          });
                        } else {
                          setFeedback({
                            type: "break_end",
                            title: `Welcome back, ${data.employee.name}`,
                            subtitle: `Break was ${data.this_break_minutes} minutes. It will be deducted from today's hours.`,
                          });
                        }
                        setPin("");
                      } catch (err) {
                        setFeedback({
                          type: "error",
                          title: "Break punch failed",
                          subtitle: formatApiErrorDetail(err?.response?.data?.detail) || "Please check in first, then try again.",
                        });
                        setPin("");
                      } finally {
                        setBusy(false);
                      }
                    }}
                    data-testid="punch-break-btn"
                    className="h-14 rounded-full bg-[#D4AF37]/15 border-2 border-[#D4AF37] text-[#D4AF37] text-sm font-medium tracking-wide hover:bg-[#D4AF37]/25 transition-colors flex items-center justify-center gap-2 disabled:opacity-40"
                  >
                    <Coffee className="w-5 h-5" aria-hidden="true" />
                    Punch Break In / Out
                  </button>
                  <button
                    type="button"
                    onClick={() => { setMode("leave"); setPin(""); setLeaveForm({ start_date: todayIso, end_date: todayIso, reason: "" }); }}
                    data-testid="open-leave-form-btn"
                    className="h-14 rounded-full border border-white/25 text-white/70 text-sm tracking-wide hover:bg-white/5 transition-colors flex items-center justify-center gap-2"
                  >
                    <CalendarDays className="w-4 h-4" aria-hidden="true" />
                    Apply for Leave
                  </button>
                </div>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => { setMode("attendance"); setPin(""); }}
                  data-testid="back-to-attendance-btn"
                  className="mb-6 text-white/60 hover:text-white text-sm flex items-center gap-2"
                >
                  <ArrowLeft className="w-4 h-4" aria-hidden="true" /> Back to punch in / out
                </button>

                <p className="text-white/60 text-xs tracking-[0.3em] uppercase mb-6 text-center">Apply for Leave</p>

                <div className="flex justify-center gap-3 mb-6" data-testid="leave-pin-dots">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div
                      key={i}
                      className={`w-3 h-3 rounded-full border-2 transition-colors ${
                        i < pin.length ? "bg-[#D4AF37] border-[#D4AF37]" : "border-white/30"
                      }`}
                      aria-hidden="true"
                    />
                  ))}
                </div>
                <div className="grid grid-cols-3 gap-3 mb-6">
                  {KEYS.map((k) => (
                    <button
                      key={k}
                      type="button"
                      data-testid={`leave-key-${k}`}
                      onClick={() => handleKey(k)}
                      className="key-press h-14 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-xl font-serif transition-colors flex items-center justify-center"
                    >
                      {k === "del" ? <Delete className="w-5 h-5" aria-hidden="true" /> : k === "clear" ? <span className="text-[10px] tracking-widest">CLR</span> : k}
                    </button>
                  ))}
                </div>

                <div className="space-y-3">
                  <div>
                    <label className="text-white/60 text-[10px] tracking-[0.25em] uppercase">From</label>
                    <input
                      ref={startRef}
                      type="date"
                      data-testid="leave-start"
                      value={leaveForm.start_date}
                      onChange={(e) => setLeaveForm({ ...leaveForm, start_date: e.target.value })}
                      className="w-full mt-1 h-12 rounded-xl bg-white/5 border border-white/10 px-4 text-white [color-scheme:dark]"
                    />
                  </div>
                  <div>
                    <label className="text-white/60 text-[10px] tracking-[0.25em] uppercase">To</label>
                    <input
                      type="date"
                      data-testid="leave-end"
                      value={leaveForm.end_date}
                      onChange={(e) => setLeaveForm({ ...leaveForm, end_date: e.target.value })}
                      className="w-full mt-1 h-12 rounded-xl bg-white/5 border border-white/10 px-4 text-white [color-scheme:dark]"
                    />
                  </div>
                  <div>
                    <label className="text-white/60 text-[10px] tracking-[0.25em] uppercase">Reason (optional)</label>
                    <textarea
                      rows={2}
                      data-testid="leave-reason"
                      value={leaveForm.reason}
                      onChange={(e) => setLeaveForm({ ...leaveForm, reason: e.target.value })}
                      placeholder="Family function, medical, etc."
                      className="w-full mt-1 rounded-xl bg-white/5 border border-white/10 px-4 py-3 text-white placeholder-white/30 resize-none"
                    />
                  </div>
                </div>

                <button
                  type="button"
                  disabled={busy || pin.length < 4}
                  data-testid="leave-submit-btn"
                  onClick={submit}
                  className="mt-6 w-full h-14 rounded-full bg-[#D4AF37] text-black text-base font-medium tracking-wide hover:bg-[#E5C349] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {busy ? "Sending…" : "Submit leave request"}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Feedback overlay */}
      {feedback && (
        <div
          className="fixed inset-0 z-20 flex items-center justify-center bg-black/70 backdrop-blur-sm px-6"
          data-testid="kiosk-feedback"
          onClick={() => setFeedback(null)}
        >
          <div className="max-w-md w-full text-center fade-up" onClick={(e) => e.stopPropagation()}>
            <div
              className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-6 ${
                feedback.type === "in"
                  ? "bg-[#D4AF37] text-black"
                  : feedback.type === "out"
                  ? "bg-white text-black"
                  : feedback.type === "error"
                  ? "bg-red-600 text-white"
                  : feedback.type === "leave"
                  ? "bg-[#D4AF37] text-black"
                  : "bg-white/20 text-white"
              }`}
            >
              {feedback.type === "in" ? (
                <LogIn className="w-8 h-8" aria-hidden="true" />
              ) : feedback.type === "out" ? (
                <LogOut className="w-8 h-8" aria-hidden="true" />
              ) : feedback.type === "leave" ? (
                <CalendarDays className="w-8 h-8" aria-hidden="true" />
              ) : feedback.type === "break_start" || feedback.type === "break_end" ? (
                <Coffee className="w-8 h-8" aria-hidden="true" />
              ) : (
                <Clock className="w-8 h-8" aria-hidden="true" />
              )}
            </div>
            <h3 className="font-serif text-4xl tracking-tight">{feedback.title}</h3>
            <p className="mt-3 text-white/70">{feedback.subtitle}</p>
            {feedback.notify && (
              <button
                type="button"
                onClick={() => openWhatsApp(feedback.notify.phone, feedback.notify.message)}
                data-testid="notify-owner-btn"
                className="mt-6 inline-flex items-center gap-2 h-12 px-6 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium transition-colors"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M20.5 3.5a11 11 0 0 0-17 13.4L2 22l5.3-1.4a11 11 0 0 0 13.2-17zM12 20a8 8 0 0 1-4.1-1.1l-.3-.2-3.1.8.8-3-.2-.3A8 8 0 1 1 12 20zm4.6-6c-.2-.1-1.5-.7-1.7-.8-.2-.1-.4-.1-.6.1-.2.3-.7.8-.8.9-.2.2-.3.2-.5.1-1.5-.7-2.4-1.3-3.4-3-.3-.5.3-.4.8-1.3.1-.2 0-.4 0-.5s-.6-1.4-.8-2c-.2-.5-.4-.4-.6-.4h-.5c-.2 0-.5.1-.7.3-.2.3-.9.9-.9 2.2 0 1.3.9 2.5 1 2.7.1.2 1.8 2.9 4.5 4 2.7 1.1 2.7.8 3.2.7.5-.1 1.5-.6 1.7-1.2.2-.6.2-1.1.2-1.2-.1-.2-.3-.2-.6-.4z"/></svg>
                Notify Owner on WhatsApp
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
